import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import MASpinner from '../common/MASpinner';
import {
  consultantCcsdPricingRuleListFetch,
  consultantCcsdPricingRuleListUnload,
  consultantCcsdPricingRuleSingleCreate,
  consultantCcsdPricingRuleSingleDelete,
} from '../../lib/redux/actions/consultantCcsdPricingRules';
import { healthInsurerListFetch, healthInsurerListUnload } from '../../lib/redux/actions/healthInsurer';

const RULE_TYPE_REFERENCE_INSURER = 'REFERENCE_INSURER';
const RULE_TYPE_HIGHEST_INSURER_RATE = 'HIGHEST_INSURER_RATE';

const defaultFormState = {
  ruleType: RULE_TYPE_REFERENCE_INSURER,
  referenceInsurer: '',
  percentageIncrease: '',
  appliesToInsurer: '',
};

const mapStateToProps = (state) => ({
  ...state.consultantCcsdPricingRules,
  healthInsurers: state.healthInsurer.healthInsurers,
  isFetchingHealthInsurers: state.healthInsurer.isFetchingHealthInsurers,
});

const mapDispatchToProps = {
  consultantCcsdPricingRuleListFetch,
  consultantCcsdPricingRuleListUnload,
  consultantCcsdPricingRuleSingleCreate,
  consultantCcsdPricingRuleSingleDelete,
  healthInsurerListFetch,
  healthInsurerListUnload,
};

class ConsultantCcsdPricingRulesContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showAddForm: false,
      form: { ...defaultFormState },
      submitError: null,
      deletingId: null,
    };
  }

  componentDidMount() {
    const { consultant, consultantCcsdPricingRuleListFetch, healthInsurerListFetch } = this.props;
    consultantCcsdPricingRuleListFetch(consultant['@id']);
    // One fetch of every insurer that takes part in pricing rules at all, filtered differently for
    // each of the two dropdowns below — see referenceInsurerOptions() and scopeInsurerOptions().
    // Fetching the wider set also lets getInsurerName() resolve an analogue scope insurer, which a
    // list filtered to isAnalogue=false could not.
    healthInsurerListFetch(1, {
      pagination: false,
      'order[name]': 'ASC',
      canHavePricingRules: true,
    });
  }

  componentWillUnmount() {
    this.props.consultantCcsdPricingRuleListUnload();
    this.props.healthInsurerListUnload();
  }

  handleFormChange(field, value) {
    this.setState((prev) => ({
      form: {
        ...prev.form,
        [field]: value,
        // HIGHEST_INSURER_RATE has no reference insurer, but keeps any percentage increase
        // entered — the uplift applies to the winning insurer's rate (MED-946).
        ...(field === 'ruleType' && value === RULE_TYPE_HIGHEST_INSURER_RATE ? { referenceInsurer: '' } : {}),
      },
      submitError: null,
    }));
  }

  handleSubmit(e) {
    e.preventDefault();
    const { consultant, consultantCcsdPricingRuleSingleCreate } = this.props;
    const { form } = this.state;

    if (form.ruleType === RULE_TYPE_REFERENCE_INSURER && !form.referenceInsurer) {
      this.setState({ submitError: 'A reference insurer is required for this rule type.' });
      return;
    }

    const data = {
      consultant: consultant['@id'],
      ruleType: form.ruleType,
    };

    if (form.ruleType === RULE_TYPE_REFERENCE_INSURER) {
      data.referenceInsurer = form.referenceInsurer;
    }

    // Optional scope (MED-987). Left off entirely when blank, so the rule applies to every column.
    if (form.appliesToInsurer !== '') {
      data.appliesToInsurer = form.appliesToInsurer;
    }

    // The percentage increase applies to both rule types (MED-946).
    if (form.percentageIncrease !== '') {
      data.percentageIncrease = parseFloat(form.percentageIncrease);
    }

    consultantCcsdPricingRuleSingleCreate(data)
      .then(() => {
        this.setState({ showAddForm: false, form: { ...defaultFormState }, submitError: null });
      })
      .catch(() => {
        this.setState({ submitError: 'There was a problem saving the rule. Please check and try again.' });
      });
  }

  handleDelete(rule) {
    if (!window.confirm('Are you sure you want to delete this pricing rule?')) {
      return;
    }
    this.setState({ deletingId: rule.id });
    this.props.consultantCcsdPricingRuleSingleDelete(rule.id).then(() => {
      this.setState({ deletingId: null });
    });
  }

  /**
   * Insurers that may be *referenced* by a rule: they must publish their own rates, so analogue
   * insurers are excluded (MED-959). Self-Pay keeps canHavePricingRules because rules still price
   * into its column (MED-946), but it has no published rates to be referenced from.
   */
  referenceInsurerOptions() {
    return (this.props.healthInsurers || []).filter((hi) => !hi.isAnalogue);
  }

  /**
   * Insurers a rule may be *scoped to* (MED-987). Deliberately wider than the reference list: the
   * only requirement is that rules price into the insurer's column at all, which Self-Pay does.
   * Scoping a rule to Self-Pay is the case this feature exists for.
   */
  scopeInsurerOptions() {
    return this.props.healthInsurers || [];
  }

  getInsurerName(insurerIri) {
    const { healthInsurers } = this.props;
    if (!insurerIri || !healthInsurers) return '—';
    const insurer = healthInsurers.find((hi) => hi['@id'] === insurerIri);
    return insurer ? insurer.name : insurerIri;
  }

  renderRuleDescription(rule) {
    // Both rule types support an optional percentage increase (MED-946)
    const base =
      rule.ruleType === RULE_TYPE_HIGHEST_INSURER_RATE
        ? 'Highest insurer rate'
        : this.getInsurerName(rule.referenceInsurer);
    const pct = rule.percentageIncrease != null ? rule.percentageIncrease : null;

    let description = base;
    if (pct !== null) {
      description += ` + ${pct}%`;
    }
    if (rule.appliesToInsurer) {
      description += ` (${this.getInsurerName(rule.appliesToInsurer)} only)`;
    }
    return description;
  }

  renderRuleRow(rule) {
    const { deletingId } = this.state;
    const isDeleting = deletingId === rule.id;
    return (
      <tr key={rule.id}>
        <td>{this.renderRuleDescription(rule)}</td>
        <td>
          <button
            className="btn btn-sm btn-outline-danger"
            disabled={isDeleting}
            onClick={() => this.handleDelete(rule)}>
            {isDeleting ? <MASpinner wrapperClasses={''} /> : <i className="fas fa-trash" />}
          </button>
        </td>
      </tr>
    );
  }

  renderAddForm() {
    const { isSavingConsultantCcsdPricingRule } = this.props;
    const { form, submitError } = this.state;
    const isReferenceInsurer = form.ruleType === RULE_TYPE_REFERENCE_INSURER;

    return (
      <Form onSubmit={this.handleSubmit.bind(this)} className="mt-3 p-3 border rounded bg-light">
        {submitError && <div className="alert alert-danger">{submitError}</div>}
        <div className="alert alert-secondary py-2 mb-3">
          <small>
            <strong>Examples:</strong>
            <ul className="mb-0 mt-1 pl-3">
              <li>
                <strong>WPA</strong> — use WPA price (reference insurer, no adjustment)
              </li>
              <li>
                <strong>Healix + 15%</strong> — reference insurer with percentage increase
              </li>
              <li>
                <strong>Highest insurer rate</strong> — use the highest price across all insurers for the code
              </li>
              <li>
                <strong>Highest insurer rate + 10%</strong> — highest price across all insurers, with a percentage
                increase
              </li>
              <li>
                <strong>WPA + 100% (Self-Pay only)</strong> — set “Applies To” to price one insurer differently. Your
                other rules keep pricing every other insurer.
              </li>
            </ul>
          </small>
        </div>
        <Row>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Rule Type</Form.Label>
              <Form.Control
                as="select"
                value={form.ruleType}
                onChange={(e) => this.handleFormChange('ruleType', e.target.value)}>
                <option value={RULE_TYPE_REFERENCE_INSURER}>Reference Insurer Rate</option>
                <option value={RULE_TYPE_HIGHEST_INSURER_RATE}>Highest Insurer Rate</option>
              </Form.Control>
            </Form.Group>
          </Col>

          {isReferenceInsurer && (
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  Reference Insurer <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="select"
                  value={form.referenceInsurer}
                  onChange={(e) => this.handleFormChange('referenceInsurer', e.target.value)}>
                  <option value="">— Select insurer —</option>
                  {this.referenceInsurerOptions().map((hi) => (
                    <option key={hi['@id']} value={hi['@id']}>
                      {hi.name}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
          )}

          <Col md={4}>
            <Form.Group>
              <Form.Label>Applies To (optional)</Form.Label>
              <Form.Control
                as="select"
                value={form.appliesToInsurer}
                onChange={(e) => this.handleFormChange('appliesToInsurer', e.target.value)}>
                <option value="">— All insurers —</option>
                {this.scopeInsurerOptions().map((hi) => (
                  <option key={hi['@id']} value={hi['@id']}>
                    {hi.name}
                  </option>
                ))}
              </Form.Control>
              <Form.Text className="text-muted">
                Limits this rule to one insurer. That rule then always applies for that insurer, in place of your other
                rules.
              </Form.Text>
            </Form.Group>
          </Col>

          <Col md={2}>
            <Form.Group>
              <Form.Label>% Increase (optional)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                placeholder="e.g. 15"
                value={form.percentageIncrease}
                onChange={(e) => this.handleFormChange('percentageIncrease', e.target.value)}
              />
            </Form.Group>
          </Col>

        </Row>

        <div className="mt-2">
          <Button type="submit" variant="primary" size="sm" disabled={isSavingConsultantCcsdPricingRule} className="mr-2">
            {isSavingConsultantCcsdPricingRule ? 'Saving…' : 'Save Rule'}
          </Button>
          <Button
            type="button"
            variant="outline-secondary"
            size="sm"
            onClick={() => this.setState({ showAddForm: false, form: { ...defaultFormState }, submitError: null })}>
            Cancel
          </Button>
        </div>
      </Form>
    );
  }

  render() {
    const { consultantCcsdPricingRules, isFetchingConsultantCcsdPricingRules } = this.props;
    const { showAddForm } = this.state;

    return (
      <div className="crud__section mb-4">
        <div className="crud__section-icon">
          <i className="fas fa-percentage" />
        </div>
        <h2 className="crud__section-header">Consultant Pricing Rules</h2>
        <p className="hint-text">
          Define fee rules applied when this consultant raises invoices against non-fee-assured insurers. The portal picks
          the rule that yields the highest price automatically.
        </p>

        {isFetchingConsultantCcsdPricingRules && <MASpinner msg="Loading pricing rules…" />}

        {!isFetchingConsultantCcsdPricingRules && consultantCcsdPricingRules && consultantCcsdPricingRules.length > 0 && (
          <Table bordered hover size="sm" className="mb-3">
            <thead className="thead-light">
              <tr>
                <th>Rule</th>
                <th />
              </tr>
            </thead>
            <tbody>{consultantCcsdPricingRules.map((rule) => this.renderRuleRow(rule))}</tbody>
          </Table>
        )}

        {!isFetchingConsultantCcsdPricingRules && (!consultantCcsdPricingRules || consultantCcsdPricingRules.length === 0) && (
          <div className="alert alert-info mb-3">No pricing rules defined for this consultant.</div>
        )}

        {!showAddForm && (
          <Button variant="outline-primary" size="sm" onClick={() => this.setState({ showAddForm: true })}>
            <i className="fas fa-plus mr-1" />
            Add Pricing Rule
          </Button>
        )}

        {showAddForm && this.renderAddForm()}
      </div>
    );
  }
}

ConsultantCcsdPricingRulesContainer.propTypes = {
  consultant: PropTypes.object.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(ConsultantCcsdPricingRulesContainer);
